<script setup>
import { useSubDomainService } from '../../services/subDomainService'
import { computed, inject, onMounted, ref } from 'vue'
import SubdomainLimitReached from './dialogs/SubdomainLimitReached.vue'
import { event } from 'vue-gtag'
import { useQuasar } from 'quasar'
import { useStore } from 'vuex'

const store = useStore()
const quasar = useQuasar()
const subdomainCount = ref(0)
const subdomainService = useSubDomainService()
const newSubdomainForm = ref(null)
const newDomain = ref(localStorage.newDomain)
const limits = computed(() => store.getters.getLimits)
const canCreateNewSubdomain = computed(() => subdomainCount?.value < limits?.value?.getSubdomainCount()?.value)
const minSubdomainLength = computed(() => parseInt(limits?.value?.getSubdomainMinLength()?.value))
const maxSubdomainLength = computed(() => parseInt(limits?.value?.getSubdomainMaxLength()?.value))
const newDomainRules = [
    val => val && val.length > 0 || 'Please enter your Domain',
    val => val && val.length >= minSubdomainLength.value || `Domain should not be shorter than ${minSubdomainLength.value} characters`,
    val => val && val.length <= maxSubdomainLength.value || `Domain should not be greater than ${maxSubdomainLength.value} characters`,
    val => isValidSubdomain(val) || 'Only allowed characters a-z,hyphen and numbers.But should not start with hyphen'
]
const emits = defineEmits(['added:subdomain'])
const appDomain = inject('appDomain')
const eventBus = inject('eventBus')
const props = defineProps({
    inline: {
        type: Boolean,
        default: false
    }
})

async function addNewDomain () {
    if (!await newSubdomainForm.value.validate()) {
        return
    }

    if (!canCreateNewSubdomain.value) {
        quasar.dialog({
            component: SubdomainLimitReached,
            componentProps: {
                subdomainCount: subdomainCount.value,
                subdomainCountLimit: limits?.value?.getSubdomainCount()?.value
            }
        })

        return
    }

    if ((newDomain.value?.trim() ?? '') === '') {
        return
    }

    quasar.loading.show()

    subdomainService
        .create(newDomain.value)
        .then((response) => {
            if (response?.name !== 'AxiosError') {
                emits('added:subdomain', response.data.data)
                quasar.notify({
                    position: 'top',
                    type: 'positive',
                    message: `New Domain "${response.data.data.name.toUpperCase()}" has been created`
                })

                newSubdomainForm.value.reset()
                delete localStorage.newDomain
                subdomainCount.value++;
                event('subdomain-create', { method: 'Google' })
                eventBus.emit('subdomain-created', response.data.data)
            } else {
                if (response?.response?.data?.errors) {
                    for (const [name, errors] of Object.entries(response.response.data.errors)) {
                        for (const error of errors)
                            quasar.notify({
                                progress: true,
                                message: error ?? 'Error while create Domain',
                                type: 'negative',
                                position: 'top',
                            })
                    }
                } else {
                    quasar.notify({
                        progress: true,
                        message: response?.response?.data?.message ?? 'Error while create Domain',
                        type: 'negative',
                        position: 'top',
                    })
                }
            }
        })
        .finally(() => quasar.loading.hide())
}

async function fetchData () {
    subdomainService
        .getAll()
        .then(data => subdomainCount.value = data.length)
        .finally(() => quasar.loading.hide())
}

function isValidSubdomain (value) {
    if (!value) {
        return false
    }

    const pattern = new RegExp('^[a-zA-Z][\\da-zA-Z\-]+$')

    return !!pattern.test(value.trim())
}

onMounted(async () => await fetchData())
</script>

<template>
    <div class="bg-white">
        <div v-if="!props.inline"
             class="q-toolbar row no-wrap items-center text-white shadow-2"
             style="background: rgb(39, 170, 225);">
            <span v-if="subdomainCount >=1">Add new Custom Domain</span>
            <span v-else>Register your own Custom Domain to create MEMO.LINKS</span>
        </div>

        <q-form ref="newSubdomainForm"
                :class="{'row items-start no-wrap': props.inline, 'q-pa-md q-card--bordered': !props.inline}"
                @reset="()=>newDomain=null">
            <q-input v-model="newDomain"
                     :rules="newDomainRules"
                     class="new-subdomain-input"
                     :class="{'col-7': props.inline}"
                     clearable
                     dense
                     :input-style="props.inline ?'width:270px': null"
                     label="Type here your new Domain" outlined/>
            <div v-if="!props.inline" class="text-blue q-my-md q-pa-sm bg-grey-1">
                https://<span class="text-deep-orange">{{ newDomain }}</span>.{{ appDomain }}/
            </div>
            <q-btn v-if="props.inline"
                   color="deep-orange"
                   label="Register"
                   class="col-4 q-ml-sm"
                   no-wrap
                   dense
                   @click="addNewDomain"/>
            <q-btn v-else
                   color="deep-orange"
                   icon="fa-solid fa-circle-plus"
                   label="Register"
                   @click="addNewDomain"/>
        </q-form>
    </div>
</template>

<style scoped>

</style>
